// src/pages/NotificationSettings.jsx
import React, { useState, useEffect } from 'react';
import {
  Mail, Bell, MessageSquare, CheckCircle2, XCircle, AlertTriangle,
  ChevronDown, Eye, EyeOff, Send, Loader, Save, RefreshCw,
  Wifi, WifiOff, Shield, Smartphone, Globe
} from 'lucide-react';
import toast from 'react-hot-toast';
import { notificationAPI } from '../services/api';

// ─── Channel card wrapper ──────────────────────────────────────────────────────
function ChannelCard({ id, icon: Icon, title, subtitle, color, enabled, onToggle, children, badge }) {
  return (
    <div
      className="rounded-2xl border overflow-hidden transition-all duration-300"
      style={{
        backgroundColor: 'var(--bg-secondary)',
        borderColor: enabled ? color : 'var(--border-color)',
        boxShadow: enabled ? `0 0 0 1px ${color}30` : 'none',
      }}
    >
      {/* Header */}
      <div className="flex items-center gap-4 p-5 border-b" style={{ borderColor: 'var(--border-color)' }}>
        <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${color}18` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-bold text-base" style={{ color: 'var(--text-primary)' }}>{title}</h3>
            {badge && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wide"
                style={{ backgroundColor: `${color}20`, color }}>
                {badge}
              </span>
            )}
          </div>
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>{subtitle}</p>
        </div>
        {/* Master toggle */}
        <button
          type="button"
          onClick={() => onToggle(!enabled)}
          className="relative flex-shrink-0 w-12 h-6 rounded-full transition-all duration-200 focus:outline-none"
          style={{
            backgroundColor: enabled ? color : 'var(--bg-tertiary)',
            border: '2px solid var(--border-color)',
          }}
        >
          <span className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200"
            style={{ transform: enabled ? 'translateX(22px)' : 'translateX(0)' }} />
        </button>
      </div>

      {/* Body — only shown when enabled */}
      {enabled && (
        <div className="p-5 space-y-4">
          {children}
        </div>
      )}

      {/* Disabled state */}
      {!enabled && (
        <div className="px-5 py-4 flex items-center gap-2">
          <WifiOff className="w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
          <span className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
            Enable to configure {title.toLowerCase()}
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Field ─────────────────────────────────────────────────────────────────────
function Field({ label, hint, error, children }) {
  return (
    <div>
      <label className="block text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: 'var(--text-secondary)' }}>
        {label}
      </label>
      {children}
      {hint && !error && <p className="text-[11px] mt-1" style={{ color: 'var(--text-tertiary)' }}>{hint}</p>}
      {error && <p className="text-[11px] mt-1 text-red-400">{error}</p>}
    </div>
  );
}

// ─── Input ─────────────────────────────────────────────────────────────────────
function Input({ type = 'text', value, onChange, placeholder, mono, readOnly, suffix }) {
  const [show, setShow] = useState(false);
  const isPassword = type === 'password';
  return (
    <div className="relative">
      <input
        type={isPassword && !show ? 'password' : 'text'}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        readOnly={readOnly}
        className={`w-full px-3 py-2.5 rounded-xl border text-sm focus:outline-none transition-colors ${mono ? 'font-mono' : ''} ${readOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
        style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)', paddingRight: isPassword || suffix ? '2.75rem' : undefined }}
      />
      {isPassword && (
        <button type="button" onClick={() => setShow(s => !s)}
          className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-tertiary)' }}>
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      )}
      {suffix && !isPassword && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold" style={{ color: 'var(--text-tertiary)' }}>
          {suffix}
        </span>
      )}
    </div>
  );
}

// ─── Status badge ──────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const map = {
    connected: { icon: CheckCircle2, color: '#22c55e', label: 'Connected' },
    error: { icon: XCircle, color: '#ef4444', label: 'Error' },
    untested: { icon: AlertTriangle, color: '#f59e0b', label: 'Not Tested' },
    testing: { icon: Loader, color: '#3b82f6', label: 'Testing...' },
  };
  const { icon: Icon, color, label } = map[status] || map.untested;
  return (
    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold"
      style={{ backgroundColor: `${color}15`, color, border: `1px solid ${color}30` }}>
      <Icon className={`w-3.5 h-3.5 ${status === 'testing' ? 'animate-spin' : ''}`} />
      {label}
    </div>
  );
}

// ─── FCM File Upload component ─────────────────────────────────────────────────
function FCMFileUpload({ hasCredentials, onUploaded }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = React.useRef();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await notificationAPI.uploadFCM(file);
      const d = res.data?.data ?? res.data;
      onUploaded(d);
      toast.success('Firebase credentials uploaded successfully');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed — check file is a valid Firebase service account JSON');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const hasFile = Boolean(hasCredentials);

  return (
    <div>
      <input ref={inputRef} type="file" accept=".json,application/json" className="hidden" onChange={handleFile} />
      <div
        className="flex items-center gap-3 p-3 rounded-xl border-2 border-dashed transition-all cursor-pointer"
        style={{ borderColor: hasFile ? '#8b5cf6' : 'var(--border-color)', backgroundColor: hasFile ? '#8b5cf608' : 'var(--bg-tertiary)' }}
        onClick={() => !uploading && inputRef.current?.click()}
      >
        {uploading ? (
          <>
            <Loader className="w-5 h-5 animate-spin flex-shrink-0" style={{ color: '#8b5cf6' }} />
            <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Uploading & validating...</span>
          </>
        ) : hasFile ? (
          <>
            <CheckCircle2 className="w-5 h-5 flex-shrink-0" style={{ color: '#22c55e' }} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Credentials on file</p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>Stored securely on the server</p>
            </div>
            <span className="text-xs font-bold flex-shrink-0" style={{ color: '#8b5cf6' }}>Replace</span>
          </>
        ) : (
          <>
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#8b5cf618' }}>
              <Shield className="w-4 h-4" style={{ color: '#8b5cf6' }} />
            </div>
            <div>
              <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Upload service account JSON</p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                Click to select — file is validated and stored securely on the server
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


const NOTIF_TYPES = [
  { key: 'risk.margin_call',   label: 'Margin Call',     desc: 'When account margin level drops below threshold' },
  { key: 'risk.liquidation',   label: 'Liquidation',     desc: 'When positions are force-closed' },
  { key: 'account.deposit',    label: 'Deposit',         desc: 'When a deposit is confirmed' },
  { key: 'account.withdrawal', label: 'Withdrawal',      desc: 'When a withdrawal is processed' },
  { key: 'trade.opened',       label: 'Trade Opened',    desc: 'When a new position is opened' },
  { key: 'trade.closed',       label: 'Trade Closed',    desc: 'When a position is closed' },
];

// ─── FCM Web Config Form ────────────────────────────────────────────────────────
function FCMWebConfigForm({ push, setPush }) {
  const [saving, setSaving]     = useState(false);
  const [expanded, setExpanded] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await notificationAPI.saveWebConfig({
        apiKey:            push.web_api_key,
        authDomain:        push.web_auth_domain,
        projectId:         push.web_project_id,
        storageBucket:     push.web_storage_bucket,
        messagingSenderId: push.web_sender_id,
        appId:             push.web_app_id,
        ...(push.web_vapid_key ? { vapidKey: push.web_vapid_key } : {}),
      });
      setPush(s => ({ ...s, has_web_config: true }));
      toast.success('Firebase web config saved');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save web config');
    } finally {
      setSaving(false);
    }
  };

  const isComplete = push.web_api_key && push.web_project_id && push.web_app_id;

  return (
    <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border-color)' }}>
      <button type="button" onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center gap-3 p-3"
        style={{ backgroundColor: 'var(--bg-tertiary)' }}>
        <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: push.has_web_config ? '#22c55e18' : '#8b5cf618' }}>
          {push.has_web_config
            ? <CheckCircle2 className="w-4 h-4" style={{ color: '#22c55e' }} />
            : <Globe className="w-4 h-4" style={{ color: '#8b5cf6' }} />}
        </div>
        <div className="flex-1 text-left">
          <p className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>Web App Config</p>
          <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
            {push.has_web_config ? `Configured — ${push.web_project_id || 'saved'}` : 'Required for browser & mobile push'}
          </p>
        </div>
        <ChevronDown className="w-4 h-4 flex-shrink-0 transition-transform"
          style={{ color: 'var(--text-tertiary)', transform: expanded ? 'rotate(180deg)' : 'none' }} />
      </button>

      {expanded && (
        <div className="p-3 space-y-3" style={{ borderTop: '1px solid var(--border-color)' }}>
          <div className="p-3 rounded-xl border" style={{ backgroundColor: '#8b5cf608', borderColor: '#8b5cf630' }}>
            <p className="text-xs font-semibold mb-1" style={{ color: '#8b5cf6' }}>Where to find these</p>
            <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
              Firebase Console → Project Settings → Your apps → Web app → SDK setup and configuration
            </p>
          </div>

          {/* Fields grid */}
          <div className="grid grid-cols-2 gap-2">
            {[
              { label: 'API Key',           key: 'web_api_key',        ph: 'AIzaSy...' },
              { label: 'Auth Domain',       key: 'web_auth_domain',    ph: 'project.firebaseapp.com' },
              { label: 'Project ID',        key: 'web_project_id',     ph: 'my-project' },
              { label: 'Storage Bucket',    key: 'web_storage_bucket', ph: 'my-project.appspot.com' },
              { label: 'Messaging Sender',  key: 'web_sender_id',      ph: '204233708823' },
              { label: 'App ID',            key: 'web_app_id',         ph: '1:204233708823:web:...' },
            ].map(({ label, key, ph }) => (
              <div key={key}>
                <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>{label}</label>
                <input value={push[key] || ''} onChange={e => setPush(s => ({ ...s, [key]: e.target.value }))}
                  placeholder={ph}
                  className="w-full text-xs font-mono px-2 py-1.5 rounded-lg"
                  style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)',
                    border: '1px solid var(--border-color)' }} />
              </div>
            ))}
          </div>

          {/* VAPID key — not in firebaseConfig, separate tab */}
          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>
              VAPID Key
              <span className="ml-1 font-normal" style={{ color: 'var(--text-tertiary)' }}>
                — Cloud Messaging tab → Web Push certificates → Generate key pair
              </span>
            </label>
            <input value={push.web_vapid_key || ''} onChange={e => setPush(s => ({ ...s, web_vapid_key: e.target.value }))}
              placeholder="BNxxxxxx..."
              className="w-full text-xs font-mono px-2 py-1.5 rounded-lg"
              style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)',
                border: '1px solid var(--border-color)' }} />
          </div>

          <button type="button" onClick={handleSave} disabled={saving || !isComplete}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all disabled:opacity-50"
            style={{ backgroundColor: '#8b5cf6', color: '#fff' }}>
            {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Web Config
          </button>
        </div>
      )}
    </div>
  );
}

function NotifTypeRow({ type, channels, enabledChannels, onChannelToggle }) {
  return (
    <div className="flex items-start gap-4 py-3 border-b last:border-0" style={{ borderColor: 'var(--border-color)' }}>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{type.label}</p>
        <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>{type.desc}</p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {channels.map(ch => {
          const active = enabledChannels.includes(ch.key);
          return (
            <button key={ch.key} type="button"
              onClick={() => onChannelToggle(type.key, ch.key)}
              title={ch.label}
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold border transition-all"
              style={{
                backgroundColor: active ? `${ch.color}18` : 'var(--bg-tertiary)',
                borderColor: active ? ch.color : 'var(--border-color)',
                color: active ? ch.color : 'var(--text-tertiary)',
              }}>
              <ch.icon className="w-3 h-3" />
              {ch.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════════════════════════════════════
export default function NotificationSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testStatus, setTestStatus] = useState({ email: 'untested', push: 'untested', sms: 'untested' });

  // ── Form state ───────────────────────────────────────────────────────────
  const [email, setEmail] = useState({
    enabled: false,
    smtp_host: '', smtp_port: 587, smtp_username: '', smtp_password: '',
    from_email: '', from_name: '', use_tls: true,
  });
  const [push, setPush] = useState({
    enabled:          false,
    has_credentials:  false,
    has_web_config:   false,
    fcm_project:      '',
    fcm_client_email: '',
    // web config fields (pasted by broker)
    web_api_key:        '',
    web_auth_domain:    '',
    web_project_id:     '',
    web_storage_bucket: '',
    web_sender_id:      '',
    web_app_id:         '',
    web_vapid_key:      '',
  });
  const [sms, setSms] = useState({
    enabled: false,
    twilio_account_sid: '', twilio_auth_token: '', twilio_phone_number: '',
  });

  // notification_types per channel: { 'risk.margin_call': ['email','push','sms'], ... }
  const [notifChannels, setNotifChannels] = useState(() => {
    const defaults = {};
    NOTIF_TYPES.forEach(t => { defaults[t.key] = ['email']; });
    return defaults;
  });

  // ── Load ─────────────────────────────────────────────────────────────────
  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await notificationAPI.getSettings();
      const d = res.data?.data ?? res.data;
      if (d.email) setEmail({ enabled: Boolean(d.email.enabled), smtp_host: d.email.smtp_host || '', smtp_port: d.email.smtp_port || 587, smtp_username: d.email.smtp_username || '', smtp_password: d.email.smtp_password || '', from_email: d.email.from_email || '', from_name: d.email.from_name || '', use_tls: Boolean(d.email.use_tls ?? true) });
      if (d.push) setPush({
        enabled:            Boolean(d.push.enabled ?? d.push.fcm_enabled),
        has_credentials:    Boolean(d.push.has_credentials),
        has_web_config:     Boolean(d.push.has_web_config),
        fcm_project:        d.push.fcm_project        || '',
        fcm_client_email:   d.push.fcm_client_email   || '',
        web_api_key:        d.push.web_api_key        || '',
        web_auth_domain:    d.push.web_auth_domain    || '',
        web_project_id:     d.push.web_project_id     || '',
        web_storage_bucket: d.push.web_storage_bucket || '',
        web_sender_id:      d.push.web_sender_id      || '',
        web_app_id:         d.push.web_app_id         || '',
        web_vapid_key:      d.push.web_vapid_key      || '',
      });
      if (d.sms) setSms({ enabled: Boolean(d.sms.sms_enabled), twilio_account_sid: d.sms.twilio_account_sid || '', twilio_auth_token: d.sms.twilio_auth_token || '', twilio_phone_number: d.sms.twilio_phone_number || '' });
      if (d.notif_channels) setNotifChannels(d.notif_channels);
    } catch (e) {
      toast.error('Failed to load notification settings');
    } finally {
      setLoading(false);
    }
  };

  // ── Save ─────────────────────────────────────────────────────────────────
  const save = async () => {
    setSaving(true);
    try {
      await notificationAPI.saveSettings({ email, push, sms, notif_channels: notifChannels });
      toast.success('Notification settings saved');
    } catch (e) {
      toast.error(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // ── Test connections ──────────────────────────────────────────────────────
  const testChannel = async (channel) => {
    setTestStatus(s => ({ ...s, [channel]: 'testing' }));
    try {
      await notificationAPI.test(channel);
      setTestStatus(s => ({ ...s, [channel]: 'connected' }));
      toast.success(`${channel.toUpperCase()} test successful — check your inbox/device`);
    } catch (e) {
      setTestStatus(s => ({ ...s, [channel]: 'error' }));
      toast.error(e.response?.data?.error || `${channel.toUpperCase()} test failed`);
    }
  };

  // ── Channel toggle for notif types ────────────────────────────────────────
  const toggleNotifChannel = (typeKey, channelKey) => {
    setNotifChannels(prev => {
      const current = prev[typeKey] || [];
      const next = current.includes(channelKey)
        ? current.filter(c => c !== channelKey).length === 0 ? current : current.filter(c => c !== channelKey)
        : [...current, channelKey];
      return { ...prev, [typeKey]: next };
    });
  };

  // Active channels list for the matrix
  const activeChannels = [
    ...(email.enabled ? [{ key: 'email', label: 'Email', icon: Mail, color: '#3b82f6' }] : []),
    ...(push.enabled  ? [{ key: 'push',  label: 'Push',  icon: Bell, color: '#8b5cf6' }] : []),
    ...(sms.enabled   ? [{ key: 'sms',   label: 'SMS',   icon: MessageSquare, color: '#10b981' }] : []),
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader className="w-8 h-8 animate-spin" style={{ color: 'var(--accent-primary)' }} />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Notification Channels</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
            Configure how your platform sends alerts to clients. Enable only the channels you've set up.
          </p>
        </div>
        <button onClick={save} disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-50 flex-shrink-0"
          style={{ backgroundColor: 'var(--accent-primary)' }}>
          {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? 'Saving...' : 'Save All'}
        </button>
      </div>

      {/* ── Email ──────────────────────────────────────────────────────── */}
      <ChannelCard id="email" icon={Mail} title="Email (SMTP)" subtitle="Send emails via your own SMTP server"
        color="#3b82f6" enabled={email.enabled} onToggle={v => setEmail(s => ({ ...s, enabled: v }))} badge="Recommended">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="SMTP Host">
            <Input value={email.smtp_host} onChange={e => setEmail(s => ({ ...s, smtp_host: e.target.value }))} placeholder="smtp.gmail.com" mono />
          </Field>
          <Field label="SMTP Port">
            <Input value={email.smtp_port} onChange={e => setEmail(s => ({ ...s, smtp_port: parseInt(e.target.value) || 587 }))} placeholder="587" mono suffix="TCP" />
          </Field>
          <Field label="Username / Email">
            <Input value={email.smtp_username} onChange={e => setEmail(s => ({ ...s, smtp_username: e.target.value }))} placeholder="you@gmail.com" />
          </Field>
          <Field label="Password / App Password">
            <Input type="password" value={email.smtp_password} onChange={e => setEmail(s => ({ ...s, smtp_password: e.target.value }))} placeholder="••••••••••••" />
          </Field>
          <Field label="From Email">
            <Input value={email.from_email} onChange={e => setEmail(s => ({ ...s, from_email: e.target.value }))} placeholder="noreply@yourdomain.com" />
          </Field>
          <Field label="From Name">
            <Input value={email.from_name} onChange={e => setEmail(s => ({ ...s, from_name: e.target.value }))} placeholder="Broker Trading" />
          </Field>
        </div>

        {/* TLS toggle */}
        <div className="flex items-center justify-between p-3 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4" style={{ color: '#3b82f6' }} />
            <div>
              <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Use TLS / STARTTLS</p>
              <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Recommended for port 587. Disable for port 465 (SSL).</p>
            </div>
          </div>
          <button type="button" onClick={() => setEmail(s => ({ ...s, use_tls: !s.use_tls }))}
            className="relative flex-shrink-0 w-11 h-6 rounded-full transition-all duration-200"
            style={{ backgroundColor: email.use_tls ? '#3b82f6' : 'var(--bg-tertiary)', border: '2px solid var(--border-color)' }}>
            <span className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200"
              style={{ transform: email.use_tls ? 'translateX(20px)' : 'translateX(0)' }} />
          </button>
        </div>

        {/* Test */}
        <div className="flex items-center gap-3 pt-1">
          <button type="button" onClick={() => testChannel('email')}
            disabled={testStatus.email === 'testing'}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold border transition-all disabled:opacity-50"
            style={{ borderColor: '#3b82f6', color: '#3b82f6', backgroundColor: '#3b82f620' }}>
            {testStatus.email === 'testing' ? <Loader className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send Test Email
          </button>
          <StatusBadge status={testStatus.email} />
        </div>
      </ChannelCard>

      {/* ── Push (FCM) ─────────────────────────────────────────────────── */}
      <ChannelCard id="push" icon={Bell} title="Push Notifications (FCM)" subtitle="Firebase Cloud Messaging for Android, iOS & Web"
        color="#8b5cf6" enabled={push.enabled} onToggle={v => setPush(s => ({ ...s, enabled: v }))}>
        <div className="p-3 rounded-xl border" style={{ backgroundColor: '#8b5cf610', borderColor: '#8b5cf630' }}>
          <p className="text-xs font-semibold mb-1" style={{ color: '#8b5cf6' }}>Setup required</p>
          <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
            Go to <strong>Firebase Console → Project Settings → Service Accounts → Generate new private key</strong>.
            Download the JSON file and upload it below.
          </p>
        </div>

        {/* Credentials file upload */}
        <Field label="Service Account Credentials">
          <FCMFileUpload
            hasCredentials={push.has_credentials}
            onUploaded={(result) => setPush(s => ({ ...s, has_credentials: true, fcm_project: result.project_id, fcm_client_email: result.client_email }))}
          />
        </Field>

        {/* Web App Config */}
        <FCMWebConfigForm push={push} setPush={setPush} />

        {/* Server credentials file */}
        {push.fcm_project && (
          <div className="flex items-center gap-3 p-3 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
            <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: '#22c55e' }} />
            <div className="min-w-0">
              <p className="text-xs font-bold" style={{ color: 'var(--text-primary)' }}>{push.fcm_project}</p>
              <p className="text-xs truncate" style={{ color: 'var(--text-tertiary)' }}>{push.fcm_client_email}</p>
            </div>
          </div>
        )}

        <div className="p-3 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
          <div className="flex items-center gap-2 mb-1">
            <Smartphone className="w-4 h-4" style={{ color: '#8b5cf6' }} />
            <p className="text-xs font-bold" style={{ color: 'var(--text-secondary)' }}>Client Token Status</p>
          </div>
          <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
            Push tokens are registered automatically when clients log in on mobile/web. No manual config needed.
          </p>
        </div>

        <div className="flex items-center gap-3 pt-1">
          <button type="button" onClick={() => testChannel('push')}
            disabled={testStatus.push === 'testing' || !push.has_credentials}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold border transition-all disabled:opacity-50"
            style={{ borderColor: '#8b5cf6', color: '#8b5cf6', backgroundColor: '#8b5cf620' }}>
            {testStatus.push === 'testing' ? <Loader className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Test Push
          </button>
          <StatusBadge status={testStatus.push} />
        </div>
      </ChannelCard>

      {/* ── SMS (Twilio) ───────────────────────────────────────────────── */}
      <ChannelCard id="sms" icon={MessageSquare} title="SMS (Twilio)" subtitle="Send SMS alerts via Twilio to clients with phone numbers"
        color="#10b981" enabled={sms.enabled} onToggle={v => setSms(s => ({ ...s, enabled: v }))}>
        <div className="p-3 rounded-xl border" style={{ backgroundColor: '#10b98110', borderColor: '#10b98130' }}>
          <p className="text-xs font-semibold mb-1" style={{ color: '#10b981' }}>Setup required</p>
          <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
            Get your credentials from <strong>console.twilio.com → Account → API keys & tokens</strong>.
            You'll also need a Twilio phone number.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Account SID" hint="Starts with AC...">
            <Input value={sms.twilio_account_sid} onChange={e => setSms(s => ({ ...s, twilio_account_sid: e.target.value }))} placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" mono />
          </Field>
          <Field label="Auth Token">
            <Input type="password" value={sms.twilio_auth_token} onChange={e => setSms(s => ({ ...s, twilio_auth_token: e.target.value }))} placeholder="••••••••••••••••" />
          </Field>
          <Field label="From Phone Number" hint="E.164 format, e.g. +12025551234">
            <Input value={sms.twilio_phone_number} onChange={e => setSms(s => ({ ...s, twilio_phone_number: e.target.value }))} placeholder="+12025551234" mono />
          </Field>
        </div>

        <div className="flex items-center gap-3 pt-1">
          <button type="button" onClick={() => testChannel('sms')}
            disabled={testStatus.sms === 'testing'}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold border transition-all disabled:opacity-50"
            style={{ borderColor: '#10b981', color: '#10b981', backgroundColor: '#10b98120' }}>
            {testStatus.sms === 'testing' ? <Loader className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send Test SMS
          </button>
          <StatusBadge status={testStatus.sms} />
        </div>
      </ChannelCard>

      {/* ── Notification Type Matrix ────────────────────────────────────── */}
      {activeChannels.length > 0 && (
        <div className="rounded-2xl border overflow-hidden" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
          <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--border-color)' }}>
            <div>
              <h3 className="font-bold" style={{ color: 'var(--text-primary)' }}>Notification Routing</h3>
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                Choose which channels each notification type uses
              </p>
            </div>
            <div className="flex items-center gap-2">
              {activeChannels.map(ch => (
                <span key={ch.key} className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-bold"
                  style={{ backgroundColor: `${ch.color}15`, color: ch.color }}>
                  <ch.icon className="w-3 h-3" /> {ch.label}
                </span>
              ))}
            </div>
          </div>
          <div className="px-5 divide-y" style={{ '--tw-divide-opacity': 1 }}>
            {NOTIF_TYPES.map(type => (
              <NotifTypeRow
                key={type.key}
                type={type}
                channels={activeChannels}
                enabledChannels={notifChannels[type.key] || []}
                onChannelToggle={toggleNotifChannel}
              />
            ))}
          </div>
        </div>
      )}

      {activeChannels.length === 0 && (
        <div className="rounded-2xl border border-dashed p-8 text-center" style={{ borderColor: 'var(--border-color)' }}>
          <Globe className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--text-tertiary)' }} />
          <p className="font-semibold" style={{ color: 'var(--text-secondary)' }}>No channels enabled</p>
          <p className="text-sm mt-1" style={{ color: 'var(--text-tertiary)' }}>
            Enable at least one channel above to configure notification routing
          </p>
        </div>
      )}

      {/* ── Bottom save ────────────────────────────────────────────────── */}
      <div className="flex justify-end pb-6">
        <button onClick={save} disabled={saving}
          className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold text-white disabled:opacity-50"
          style={{ backgroundColor: 'var(--accent-primary)' }}>
          {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
}